import { Box } from '@mui/material'
import { SparklesIcon } from '@heroicons/react/24/solid'
import { INTERAX_COLORS, INTERAX_SHADOWS } from '../../../../../constants/colors'

export interface AriaFaceProps {
  /** Soft pulse while Aria is thinking / speaking. */
  speaking?: boolean
  size?: number
}

const DEFAULT_ORB_SIZE_PX = 96
/** Ring thicknesses as a fraction of the orb, so the pulse scales with any size. */
const SPEAKING_RING_RATIO = 0.06
const PULSE_RING_RATIO = 0.12
/** The glyph sits at half the orb's diameter. */
const GLYPH_RATIO = 0.5
const PULSE_DURATION_S = 1.6

/**
 * Aria's avatar — an amber gradient orb with a spark glyph, not a face. A gentle ring pulses while she
 * is active.
 */
export const AriaFace = ({ speaking = false, size = DEFAULT_ORB_SIZE_PX }: AriaFaceProps) => {
  const speakingRing = `0 0 0 ${size * SPEAKING_RING_RATIO}px ${INTERAX_COLORS.sun20}`
  const pulseRing = `0 0 0 ${size * PULSE_RING_RATIO}px ${INTERAX_COLORS.sunFaded}`

  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: INTERAX_COLORS.gradientAriaOrb,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        boxShadow: speaking ? `${speakingRing}, ${INTERAX_SHADOWS.ariaOrb}` : INTERAX_SHADOWS.ariaOrb,
        '@keyframes ariaPulse': {
          '0%': { boxShadow: `0 0 0 0 ${INTERAX_COLORS.sun40}, ${INTERAX_SHADOWS.ariaOrb}` },
          '70%': { boxShadow: `${pulseRing}, ${INTERAX_SHADOWS.ariaOrb}` },
          '100%': { boxShadow: `0 0 0 0 ${INTERAX_COLORS.sunFaded}, ${INTERAX_SHADOWS.ariaOrb}` }
        },
        animation: speaking ? `ariaPulse ${PULSE_DURATION_S}s ease-out infinite` : 'none'
      }}
    >
      <SparklesIcon width={size * GLYPH_RATIO} color={INTERAX_COLORS.offBlack} />
    </Box>
  )
}

export default AriaFace
